import { useState } from "react";
import { useMatch } from "react-router-dom";
import InferenceChat from "./InferenceChat";

/**
 * Global interrogate drawer — slides out from the LEFT edge on any case page.
 * A persistent handle opens a case-wide grounded chat: ask about any decision
 * the engine made, push back, surface evidence gaps, leave feedback. The
 * per-inference panels (Archers, etc.) stay context-specific; this is the
 * always-available "ask anything about this case" entry point.
 * Hidden when not inside a case (no caseId in the route).
 */
export default function InferenceDrawer() {
  const sub = useMatch("/case/:caseId/*");
  const root = useMatch("/case/:caseId");
  const caseId = sub?.params.caseId ?? root?.params.caseId;
  const [open, setOpen] = useState(false);

  if (!caseId) return null;

  return (
    <>
      {/* left-edge handle (hidden while the drawer is open) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Interrogate the analysis"
          className="fixed left-0 top-1/2 z-50 -translate-y-1/2 rounded-r border border-l-0 border-blue-bright/50 bg-iron/90 px-1.5 py-5 backdrop-blur-sm transition-colors hover:bg-iron-2/90"
        >
          <span className="font-display text-sm font-light uppercase tracking-[0.2em] text-blue-bright [writing-mode:vertical-rl] rotate-180">
            interrogate
          </span>
        </button>
      )}

      {/* backdrop */}
      {open && <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setOpen(false)} />}

      {/* drawer */}
      <aside
        className={`fixed left-0 top-0 z-[60] flex h-full w-[380px] max-w-[88vw] flex-col border-r border-steel-dark bg-charcoal/95 backdrop-blur-md transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-steel-dark/70 px-4 py-3">
          <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">
            interrogate the analysis
          </span>
          <button type="button" onClick={() => setOpen(false)} className="font-display text-base text-bone-mute/70 hover:text-bone-dim">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {open && (
            <InferenceChat
              caseId={caseId}
              surface="case_general"
              refId={null}
              label="This case"
              intro="Ask anything about the case, the evidence, or a decision the engine made. Answers are grounded in the record and cited; if something's missing, I'll tell you what evidence to add."
              placeholder="Ask about the case or any decision…"
            />
          )}
        </div>
      </aside>
    </>
  );
}
